// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductScratchAndDentBadge, {
    ScratchAndDentStyles,
} from "@plumberssupply/App/Components/Product/ProductScratchAndDentBadge";
import RoundToBox, { RoundToBoxStyles } from "@plumberssupply/App/Components/Product/RoundToBox";
import { getRoundToBox, getShowRoundToBox } from "@plumberssupply/App/Store/Selectors/CustomProductDetailsSelectors";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsCartActionsStyles {
    wrapper?: InjectableCss;
    priceRoundToBoxWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
    scratchAndDentBadge?: ScratchAndDentStyles;
    quantityBreakPricingWrapper?: InjectableCss;
    variantOptionsWrapper?: InjectableCss;
    bundleSectionOptionsWrapper?: InjectableCss;
    deliveryScheduleButtonWrapper?: InjectableCss;
    addToListLinkWrapper?: InjectableCss;
    roundToBox?: RoundToBoxStyles;
    qtyAddWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
}

export const productDetailsCartActionsStyles: ProductDetailsCartActionsStyles = {
    priceRoundToBoxWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-wrap: wrap;
            row-gap: 5px;
        `,
    },
    roundToBox: {
        wrapper: {
            css: css`
                margin-bottom: 18px;
            `,
        },
    },
    scratchAndDentBadge: {
        wrapper: {
            css: css`
                margin-bottom: 16px;
                margin-top: 0;
            `,
        },
        link: {
            typographyProps: {
                size: 14,
            },
        },
    },
    qtyAddWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 15px;
            border-top: 1px solid ${color("common.ui7")};
        `,
    },
    addToCartWrapper: {
        css: css`
            width: 171px;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    qtyWrapper: {
        css: css`
            & > div {
                margin-right: 20px;
            }
        `,
    },
};

const styles = productDetailsCartActionsStyles;

const ProductDetailsCartActions: FC<OwnProps> = ({ product }) => {
    const [roundToBox, setRoundToBox] = useState(getRoundToBox(product?.properties));

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.priceRoundToBoxWrapper}>
                <StyledWrapper {...styles.priceWrapper}>
                    {createWidgetElement("ProductDetails/ProductDetailsPrice", {
                        fields: {},
                    })}
                </StyledWrapper>
                {getShowRoundToBox(product.properties) && (
                    <RoundToBox
                        isChecked={roundToBox}
                        sizeVariant="small"
                        toggleHandler={setRoundToBox}
                        extendedStyles={styles.roundToBox}
                    />
                )}
            </StyledWrapper>
            <ProductScratchAndDentBadge inPDP={true} extendedStyles={styles.scratchAndDentBadge} />
            <StyledWrapper {...styles.quantityBreakPricingWrapper}>
                {createWidgetElement("ProductDetails/ProductDetailsQuantityBreakPricing", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.variantOptionsWrapper}>
                {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.bundleSectionOptionsWrapper}>
                {createWidgetElement("ProductDetails/ProductDetailsBundleSectionOptions", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.qtyAddWrapper}>
                <StyledWrapper {...styles.qtyWrapper}>
                    {createWidgetElement("ProductDetails/ProductDetailsQuantityOrdered", {
                        fields: {},
                    })}
                </StyledWrapper>
                <StyledWrapper {...styles.addToCartWrapper}>
                    {createWidgetElement("ProductDetails/ProductDetailsAddToCartButton", {
                        fields: {
                            isRoundToBox: roundToBox,
                        },
                    })}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.deliveryScheduleButtonWrapper}>
                {createWidgetElement("ProductDetails/ProductDetailsDeliveryScheduleButton", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.addToListLinkWrapper}>
                {createWidgetElement("ProductDetails/ProductDetailsAddToListLink", {
                    fields: {
                        isRoundToBox: roundToBox,
                    },
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsCartActions),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        displayName: "Product Details Cart Actions",
    },
};

export default widgetModule;
